// Déplacement du mail d'origine vers les Éléments supprimés après un rangement,
// quand l'utilisateur a activé la préférence.
//
// FACTORISÉ ICI parce que DEUX chemins de rangement en ont besoin — « Ranger
// ici » / multi-sélection (useFileMail) et le glisser-déposer (useDropFiler) — et
// que la logique subtile de cette opération ne doit exister qu'en un exemplaire :
// dupliquée, elle divergerait, et c'est le geste le plus lourd de conséquences de
// l'add-in.
//
// CE QU'ELLE GARANTIT :
//  - UNE SEULE acquisition de jeton par lot, paresseuse. L'acquisition peut ouvrir
//    une invite de consentement Microsoft ; une par mail sur une sélection de
//    vingt serait intenable, et en déclencher une alors que la préférence est
//    éteinte serait injustifiable.
//  - un échec ne fait JAMAIS échouer le rangement : il est rendu comme un motif
//    que l'appelant affiche, pas comme une exception.
//  - c'est un DÉPLACEMENT vers les Éléments supprimés, récupérable depuis Outlook,
//    et non une purge définitive.
import { acquireMailToken } from '../auth/msalClient';
import { moveMessageToDeletedItems } from '../graph/sendAndFile';
import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';
import { reportError } from '../errors/errorReporter';

/**
 * Supprime le mail source d'`itemId`. Renvoie le motif d'échec, ou `undefined`
 * quand il n'y avait rien à faire ou que tout s'est bien passé.
 */
export type SourceDeleter = (itemId: string) => Promise<string | undefined>;

/**
 * Fabrique le suppresseur d'UN LOT de rangement. À appeler une fois par lot :
 * c'est la fermeture rendue ici qui porte le jeton mutualisé.
 *
 * `enabled` à faux rend une fonction inerte plutôt que `null`, pour que les
 * appelants n'aient pas à répéter le test à chaque mail.
 */
export function createSourceDeleter(enabled: boolean): SourceDeleter {
  if (!enabled) return async () => undefined;

  let token: string | null = null;
  let tokenFailed = false;
  const getToken = async (): Promise<string | null> => {
    if (token) return token;
    if (tokenFailed) return null;
    try {
      token = await acquireMailToken();
      return token;
    } catch (err) {
      // Mémoïsé : sans cela, un refus de consentement rouvrirait l'invite pour
      // chacun des mails restants du lot.
      tokenFailed = true;
      const e = err as { name?: string; message?: string; errorCode?: string };
      addBreadcrumb({
        category: 'auth',
        message: 'mail token unavailable, skipping deletion',
        data: { message: e?.message ?? String(err) },
      });
      // SIGNALÉ, et c'est le rapport le plus utile de tout ce fichier : c'est ici
      // qu'atterrit un consentement refusé ou exigeant un administrateur, et le
      // `errorCode` MSAL est ce qui distingue « l'admin doit accorder » d'« un
      // scope absent de l'app ». Sans ce rapport l'échec ne laissait qu'une
      // miette, jamais envoyée puisque le rangement réussissait.
      reportError({
        error: { name: e?.name ?? 'MailTokenError', message: e?.message ?? String(err) },
        severity: 'warning',
        isHandled: true,
        tags: [
          'delete-after-filing',
          'mail-token',
          e?.errorCode ? `msal:${e.errorCode}` : 'msal:unknown',
        ],
      });
      return null;
    }
  };

  return async (itemId: string): Promise<string | undefined> => {
    try {
      const bearer = await getToken();
      if (!bearer) return 'autorisation Microsoft indisponible';
      // Office rend un identifiant EWS ; Graph exige la forme REST.
      const restId = Office.context.mailbox.convertToRestId(
        itemId,
        Office.MailboxEnums.RestVersion.v2_0,
      );
      await moveMessageToDeletedItems(restId, bearer);
      return undefined;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      addBreadcrumb({
        category: 'mutation',
        message: 'delete-after-filing failed',
        data: { message },
      });
      reportError({
        error: err instanceof Error ? err : { name: 'DeleteSourceError', message },
        severity: 'warning',
        isHandled: true,
        tags: ['delete-after-filing', 'graph-move'],
      });
      return message;
    }
  };
}
